/**
 * Route flying: what a flight does when nobody is shooting at it.
 *
 * The dogfight AI in `ai-pilot.ts` is entirely reactive — it needs an opponent
 * to have an opinion about. That is the right model for a duel and the wrong
 * one for every other mission type: a recon two-seater has to actually cross
 * the lines, an escort has to stay with it, and a patrol has to fly its legs.
 * So a flight may carry a **route**, and the pilot flies it whenever it is not
 * engaged.
 *
 * The control law is the same one every manoeuvre script uses (`autopilot.ts`):
 * roll to a bank, hold an attitude, coordinate the rudder. Nothing here moves
 * an aeroplane directly — a route is flown through `step()` like everything
 * else, which means a laden two-seater genuinely cannot hold the climb a scout
 * can, and the route degrades honestly instead of being teleported along.
 *
 * **The flight crabs** (AD-007k §9). Wind moves the air mass the aeroplane is
 * flying in, so a pilot who points the nose at the turn point arrives downwind
 * of it. A 1918 pilot navigating by ground reference sees that happening and
 * holds off into it, and so does this one: `crabHeadingRad` offsets the
 * commanded heading by the wind-triangle angle, so what runs to the waypoint is
 * the **ground track**. Nothing about it is an oracle — the wind it corrects for
 * is `readout.windMps`, the vector `step()` has already been adding to this
 * aeroplane's own flight path.
 *
 * Determinism: no random draws at all. A route is a function of position, and
 * of the wind that position is drifting in.
 */

import { headingAngle, type Vec3 } from "../flight/math3d.ts";
import type { FlightInputs } from "../flight/inputs.ts";
import type { SimState } from "../flight/state.ts";
import type { Tunables } from "../flight/tunables.ts";
import { clamp } from "../flight/units.ts";
import {
  coordinateRudder,
  inputs,
  pitchToAttitude,
  rollToBank,
  sustainableBankRad,
  wrapAngle,
} from "./autopilot.ts";

export interface Waypoint {
  readonly position: Vec3;
  /** Shown on the HUD's waypoint indicator, e.g. `"TARGET"` or `"HOME"`. */
  readonly label: string;
  /**
   * True on the one leg a flight is not finished with the moment it arrives: a
   * **bomb run** (AD-007k §10). Arrival is what ends every other leg, and for a
   * bomb run it is the wrong question — the leg's job is to empty a rack, and
   * the rack is what says whether that has happened.
   *
   * Only the raid generators set it (`raid-parts.ts`), so every other route in
   * the game is untouched, and the flag alone does nothing: `advanceRoute` still
   * has to be *told* the run is pending (`bombRunPending` in `bomb-pass.ts`),
   * which keeps the route module free of any knowledge of racks and targets.
   */
  readonly bombRun?: boolean;
}

/**
 * What a flight does when the route and a fight want different things.
 *
 * `engage` is the fighter's answer: break off, deal with it, pick the route
 * back up. `pressOn` is the two-seater's — a recon crew whose whole job is to
 * be somewhere else does not turn and dogfight. Without the distinction an
 * escort mission deadlocks: the aeroplane being escorted stops flying its route
 * the moment the first scout appears and never starts again, because it can
 * neither win the fight nor outrun it.
 *
 * `defend` is the bomber's, and it exists because `pressOn` says two things at
 * once — *stay on the route* and *hold your fire* — which is right for a recon
 * crew and wrong for a Gotha. The manual is explicit that the heavies were
 * *"bristling with machine guns … three separate gunners"* and that *"the
 * Handley Page was more heavily armed than the Gotha, as it mounted dual guns on
 * each gun swivel"* (docs/game/manual/03-flight-reference.md), and the fleet gun
 * table has all three stations with their real traverse arcs. A `defend` flight
 * therefore flies its legs exactly as `pressOn` does and works the trigger
 * exactly as an engaged pilot does — it never manoeuvres to shoot, which is the
 * whole difference between a bomber and a fighter.
 */
export const ROUTE_ORDERS = ["engage", "pressOn", "defend"] as const;

export type RouteOrders = (typeof ROUTE_ORDERS)[number];

export interface RouteState {
  readonly waypoints: readonly Waypoint[];
  /** Index of the leg being flown; equals `waypoints.length` once the route is done. */
  readonly index: number;
  readonly complete: boolean;
}

export function createRoute(waypoints: readonly Waypoint[]): RouteState {
  return { waypoints, index: 0, complete: waypoints.length === 0 };
}

export function currentWaypoint(route: RouteState | null): Waypoint | null {
  if (!route || route.complete) return null;
  return route.waypoints[route.index] ?? null;
}

/** Ground-plane distance. Capture is horizontal on purpose — see `advanceRoute`. */
export function horizontalDistanceM(from: Vec3, to: Vec3): number {
  return Math.hypot(to.x - from.x, to.z - from.z);
}

/** Compass heading from `from` to `to`, in the engine's convention (0 = −Z, +π/2 = +X). */
export function bearingToRad(from: Vec3, to: Vec3): number {
  return Math.atan2(to.x - from.x, -(to.z - from.z));
}

/**
 * True when the waypoint is **behind** the flight — the along-track component of
 * the offset from it has gone positive, measured in the ground plane against the
 * direction the aeroplane is actually going.
 *
 * This is what "the run is over" means for a bomber, and it is deliberately not
 * a distance: a bomb leads its aeroplane by `speed × fall time` ≈ 300 m, so the
 * whole release window is flown *inside* the 400 m capture radius, and any test
 * that fires on approach fires in the middle of it.
 */
function isAstern(position: Vec3, velocity: Vec3, waypoint: Vec3): boolean {
  return (position.x - waypoint.x) * velocity.x + (position.z - waypoint.z) * velocity.z > 0;
}

/** What a bomb-run leg needs to know about the rack it is being flown for. */
export interface BombRunHold {
  /** The rack still has bombs, and something on its aiming list to put them on. */
  readonly pending: boolean;
  /** The flight's own velocity — what decides whether the target is still ahead. */
  readonly velocity: Vec3;
}

/**
 * Advances the leg counter if the aeroplane has arrived. Arrival is measured in
 * the *horizontal* plane: a two-seater that is 300 m low on its climb has still
 * flown over the turn point, and testing the full 3-D distance would leave it
 * orbiting a waypoint it can no longer reach.
 *
 * **A bomb run is the exception, and it is the whole reason `bombRun` exists**
 * (AD-007k §10). Capture at `routeCaptureRadiusM` fires 400 m out, but a bomb
 * leads its bomber by about 300 m of fall, so the release window sits *inside*
 * that circle: the old rule turned the formation for home a second before the
 * lever would have moved, the solution was taken in the turn-away, and in still
 * air the best it ever read was 31 m against a 45 m tolerance. A held run
 * instead ends where a real one does — **when the target has gone under the
 * nose and out behind** — so the whole window is flown straight and a six-bomb
 * rack empties on one pass at `bombReleaseIntervalSeconds`.
 *
 * `hold.pending` is the caller's answer to "has this rack still got a job here?"
 * (`bombRunPending`), and when it goes false the leg captures on the ordinary
 * radius again: a bomber whose rack is empty, or whose targets are all alight,
 * turns for home at once instead of flying a pointless overflight.
 */
export function advanceRoute(
  route: RouteState,
  position: Vec3,
  captureRadiusM: number,
  hold?: BombRunHold,
): RouteState {
  const waypoint = currentWaypoint(route);
  if (!waypoint) return route.complete ? route : { ...route, complete: true };
  const held = hold !== undefined && hold.pending && waypoint.bombRun === true;
  if (held) {
    if (!isAstern(position, hold.velocity, waypoint.position)) return route;
  } else if (horizontalDistanceM(position, waypoint.position) > captureRadiusM) {
    return route;
  }
  const index = route.index + 1;
  return { waypoints: route.waypoints, index, complete: index >= route.waypoints.length };
}

/**
 * The most crab the navigation law will ever hold, as the *sine* of the angle —
 * i.e. the largest ratio of cross-track wind to airspeed it will try to answer.
 * 0.5 is 30°, which is far outside anything the model can produce (the manual's
 * candidate 6 m/s against a two-seater's 30 m/s cruise is 0.2, about 11.5°) and
 * exists only so a degenerate call cannot ask `asin` for a number it does not
 * have: below this ratio the wind triangle closes and the aeroplane can make the
 * track good, above it there is no heading that holds the track at all and the
 * honest answer is "point as far upwind as a cruise leg sensibly can".
 */
const MAX_CRAB_SINE = 0.5;

/**
 * The heading to hold so the **ground track** runs along `trackRad`, given the
 * wind and the speed the aeroplane is making through the air.
 *
 * The wind triangle, and nothing more: the cross-track component of the wind has
 * to be cancelled by the cross-track component of the aeroplane's own air
 * velocity, so `V·sin(heading − track) + crosswind = 0`. `airspeedMps` is the
 * **horizontal** air-relative speed, because the triangle is solved in the
 * ground plane and a climbing aeroplane makes less of its airspeed good across
 * the ground.
 *
 * Zero wind returns `trackRad` **identically** — the same double, not a rounded
 * one — so a flight in still air flies exactly the route it flew before this
 * existed. That is asserted directly (`combat-route.test.ts`) because it is what
 * keeps every seed-pinned mission fixture valid at the shipped
 * `windReferenceMps: 0`.
 */
export function crabHeadingRad(trackRad: number, windMps: Vec3, airspeedMps: number): number {
  if (windMps.x === 0 && windMps.z === 0) return trackRad;
  // Unit vector to the right of the track is (cos track, sin track) in this
  // engine's heading convention (0 = −Z, +π/2 = +X), so this is the component of
  // the wind pushing the aeroplane off its track to the right.
  const crosswindMps = windMps.x * Math.cos(trackRad) + windMps.z * Math.sin(trackRad);
  const ratio = clamp(crosswindMps / Math.max(airspeedMps, 1), -MAX_CRAB_SINE, MAX_CRAB_SINE);
  return trackRad - Math.asin(ratio);
}

/** Speed the aeroplane is making through the air *across the ground* — the wind triangle's other side. */
function horizontalAirspeedMps(sim: SimState): number {
  return Math.hypot(sim.velocity.x, sim.velocity.z);
}

/** Radians of bank commanded per radian of heading error. */
const ROUTE_BANK_GAIN = 1.6;
/** Never bank harder than this on a cruise leg, however far off heading. */
const ROUTE_MAX_BANK_RAD = 0.7;
/** Never command a steeper climb or descent than this on a cruise leg. */
const ROUTE_MAX_PITCH_RAD = 0.16;
/** Cruise setting for a flight that is on altitude and on heading. */
const ROUTE_CRUISE_THROTTLE = 0.85;
/** Below this fraction of Vmax the flight stops cruising and asks for everything. */
const ROUTE_LOW_SPEED_FRACTION = 0.6;

/**
 * Bank the flight should be holding to close `headingErrorRad`, energy-limited.
 *
 * Note the plain `clamp` rather than `limitBank`. `limitBank` re-wraps its
 * argument, which is right for a *target bank angle* (pursuit.ts feeds it one)
 * and catastrophic for a gain-multiplied *error*: a heading error near ±π times
 * a gain of 1.6 wraps past π and comes back with the opposite sign, so a flight
 * pointed exactly away from its next waypoint commands hard left and hard right
 * on alternate ticks, averages zero bank, and flies away in a dead straight
 * line for ever. `headingErrorRad` arrives already wrapped into (−π, π], so
 * clamping its magnitude is both correct and monotone.
 */
export function routeBankRad(sim: SimState, headingErrorRad: number): number {
  const ceiling = Math.min(sustainableBankRad(sim), ROUTE_MAX_BANK_RAD);
  return clamp(headingErrorRad * ROUTE_BANK_GAIN, -ceiling, ceiling);
}

/** Pitch attitude that trades `altitudeErrorM` for a climb or descent, bounded. */
export function routePitchRad(altitudeErrorM: number, tunables: Tunables): number {
  const raw = altitudeErrorM / Math.max(tunables.routeAltitudeToPitchM, 1);
  return clamp(raw, -ROUTE_MAX_PITCH_RAD, ROUTE_MAX_PITCH_RAD);
}

/**
 * One tick of route flying: turn onto the leg, hold the leg's altitude, cruise.
 * A flight that is off heading rolls into the turn and stops climbing while it
 * does — which is what makes a formation crossing the lines look like it is
 * navigating rather than sliding along a spline.
 *
 * The heading it turns onto is the crabbed one, so in wind the nose sits off the
 * bearing to the waypoint by the drift angle and the aeroplane still arrives.
 */
export function routeInputs(sim: SimState, waypoint: Waypoint, tunables: Tunables): FlightInputs {
  const track = bearingToRad(sim.position, waypoint.position);
  const heading = crabHeadingRad(track, sim.readout.windMps, horizontalAirspeedMps(sim));
  const headingError = wrapAngle(heading - headingAngle(sim.orientation));
  const bank = routeBankRad(sim, headingError);
  // Climb demand fades out in a hard turn: a banked aeroplane asked to hold
  // both loses speed and gets neither.
  const turnScale = 1 - Math.min(Math.abs(bank) / ROUTE_MAX_BANK_RAD, 1) * 0.75;
  const pitch = routePitchRad((waypoint.position.y - sim.position.y) * turnScale, tunables);
  const slow = sim.readout.airspeedMps < sim.config.spec.vmaxMps * ROUTE_LOW_SPEED_FRACTION;
  return inputs({
    pitch: pitchToAttitude(sim, pitch, tunables),
    roll: rollToBank(sim, bank, tunables),
    yaw: coordinateRudder(sim, tunables),
    throttle: slow || pitch > 0 ? 1 : ROUTE_CRUISE_THROTTLE,
  });
}

/** Straight and level on the current heading — what a flight flies once its route runs out. */
export function holdCourseInputs(sim: SimState, tunables: Tunables): FlightInputs {
  return inputs({
    pitch: pitchToAttitude(sim, 0, tunables),
    roll: rollToBank(sim, 0, tunables),
    yaw: coordinateRudder(sim, tunables),
    throttle: ROUTE_CRUISE_THROTTLE,
  });
}
